import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../utils/api';
// Phase 7 sign-off: profiles short_text, csv_cell (HTML body).
import { MAX_LENGTHS } from '../../utils/inputValidation';

export default function ComposeTab() {
  const [subject, setSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [target, setTarget] = useState('all');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3000);
  };

  const loadTemplates = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/admin/templates');
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const handleLoadTemplate = async (name) => {
    setSelectedTemplate(name);
    if (!name) return;
    try {
      const res = await apiFetch(`/api/v1/admin/templates/${name}`);
      if (res.ok) {
        const data = await res.json();
        setHtmlContent(data.html || '');
      }
    } catch { showToast('error', 'Failed to load template'); }
  };

  const handleSend = async () => {
    setShowConfirm(false);
    if (!subject.trim() || !htmlContent.trim()) {
      showToast('error', 'Subject and HTML content are required');
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const res = await apiFetch('/api/v1/admin/newsletter/send', {
        method: 'POST',
        body: JSON.stringify({ subject, html_content: htmlContent, target }),
      });
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        showToast('success', `Newsletter sent: ${data.sent} delivered`);
      } else {
        const data = await res.json();
        showToast('error', data.detail || 'Send failed');
      }
    } catch { showToast('error', 'Network error'); }
    setSending(false);
  };

  const targetLabels = { all: 'All (Subscribers + Users)', subscribers_only: 'Subscribers Only', users_only: 'Users Only' };

  return (
    <div>
      {toast && (
        <div className={`em-toast em-toast-${toast.type}`}>
          {toast.msg}
          <button onClick={() => setToast(null)}>×</button>
        </div>
      )}

      <div className="em-compose">
        <div className="em-form-group">
          <label>Subject *</label>
          <input type="text" value={subject} onChange={e => setSubject(e.target.value)} maxLength={MAX_LENGTHS.short_text} placeholder="Newsletter subject line" />
        </div>

        <div className="em-form-group">
          <label>Target Audience</label>
          <div className="em-radio-group">
            {Object.entries(targetLabels).map(([val, label]) => (
              <label key={val} className="em-radio-label">
                <input type="radio" name="target" value={val} checked={target === val} onChange={e => setTarget(e.target.value)} />
                {label}
              </label>
            ))}
          </div>
        </div>

        <div className="em-form-group">
          <label>Load from Template (optional)</label>
          <select value={selectedTemplate} onChange={e => handleLoadTemplate(e.target.value)}>
            <option value="">— None —</option>
            {templates.map(t => (
              <option key={t.name} value={t.name}>{t.name}</option>
            ))}
          </select>
        </div>

        <div className="em-form-group">
          <label>HTML Content *</label>
          <textarea
            value={htmlContent}
            onChange={e => setHtmlContent(e.target.value)}
            maxLength={MAX_LENGTHS.csv_cell}
            placeholder="<h1>Your newsletter content here...</h1>"
            spellCheck={false}
          />
        </div>

        {htmlContent && (
          <div className="em-form-group">
            <label>Preview</label>
            <div className="em-preview-pane" style={{ height: 300 }}>
              <iframe
                title="Newsletter Preview"
                srcDoc={htmlContent}
                style={{ width: '100%', height: '100%', border: 'none' }}
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 16 }}>
          <button
            className="em-btn em-btn-primary"
            onClick={() => setShowConfirm(true)}
            disabled={sending || !subject.trim() || !htmlContent.trim()}
          >
            {sending ? 'Sending...' : 'Send Newsletter'}
          </button>

          {result && (
            <div className="em-send-result">
              <span className="em-badge em-badge-sent">{result.sent} sent</span>
              {result.failed > 0 && <span className="em-badge em-badge-bounced">{result.failed} failed</span>}
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                of {result.total_recipients} recipients
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="em-modal-overlay" onClick={() => setShowConfirm(false)}>
          <div className="em-modal" onClick={e => e.stopPropagation()}>
            <h3>Confirm Send</h3>
            <p>You are about to send a newsletter to <strong>{targetLabels[target]}</strong>.</p>
            <p><strong>Subject:</strong> {subject}</p>
            <p>This action cannot be undone. Continue?</p>
            <div className="em-modal-actions">
              <button className="em-btn" onClick={() => setShowConfirm(false)}>Cancel</button>
              <button className="em-btn em-btn-danger" onClick={handleSend}>Send Now</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
